// src/components/IcdRankingTable.tsx
// DPC分析タブ: ICD・病名別 退院数ランキング (表6の左)。
// 008_dpc(様式1)の医療資源病名ICD。修飾語分散は ICD コードで束ね、代表病名(mode)を表示。
// dpc6 選択時はその疾患配下の ICD 内訳に絞られる(主表/他表と同 filter)。
import { useFilters } from "../store";
import { useDiseaseRankingIcd } from "../lib/useDiseaseRankingIcd";
import { CsvButton } from "./CsvButton";
import { useSort, SortTh } from "../lib/useSort";
import type { DiseaseRankingIcdRow as IcdRow } from "@shared/api";
import { useDpcAgg } from "../lib/useDpcAgg";

// [2026-09-29 ユーザ指示] 在院中央値 列は削除 (CSV には残す)。
export function IcdRankingTable() {
  const f = useFilters();
  const { data, isLoading, error } = useDiseaseRankingIcd();
  const rows = data?.rows ?? [];
  const s = useSort(rows);
  const agg = useDpcAgg();   // [2026-09-28] 主表と同じ 期間累計/月平均

  if (error) {
    return (
      <section className="card p-2 text-xs text-red-700 min-w-0">
        <div className="font-semibold mb-1">ICD別取得エラー</div>
        <pre className="whitespace-pre-wrap">{(error as Error).message}</pre>
      </section>
    );
  }

  return (
    <section className="card p-2 flex flex-col min-w-0">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <h3 className="text-sm font-semibold" title="実績ICD病名ごとの急性期退院数。主表と同じく サイドバー「件数・金額の表示」に従う。">🩺 ICD・病名別 急性期退院数（{agg.label}）</h3>
        {f.dpc6 && (
          <span className="perf-pill bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
            疾患: {f.dpc6}
          </span>
        )}
        {isLoading && <span className="text-[10px] text-slate-400">loading…</span>}
        <CsvButton
          rows={rows.map((r) => ({
            ICDコード: r.icd,
            病名: r.byomei,
            退院数: r.n_cases,
            入院延日数: r.los_total,
            入院日数中央値: r.alos,
            期間累計_入院収入_円: r.revenue_total,
          }))}
          filename={`icd_ranking${f.dpc6 ? "_" + f.dpc6 : ""}`}
          style={{ marginLeft: "auto" }}
        />
      </div>
      <div className="overflow-x-auto overflow-y-auto max-h-[420px]">
        <table className="text-xs border-collapse w-full">
          <thead>
            <tr className="bg-slate-100 sticky top-0">
              <SortTh s={s} sortKey="icd" get={(r) => (r as IcdRow).icd} className="px-2 py-1 border-b border-slate-200 text-left whitespace-nowrap">ICD</SortTh>
              <SortTh s={s} sortKey="byomei" get={(r) => (r as IcdRow).byomei} className="px-2 py-1 border-b border-slate-200 text-left">病名</SortTh>
              <SortTh s={s} sortKey="n_cases" get={(r) => (r as IcdRow).n_cases} className="px-2 py-1 border-b border-slate-200 text-right whitespace-nowrap">退院数</SortTh>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !isLoading && (
              <tr><td colSpan={3} className="text-center text-slate-400 py-4">該当データなし</td></tr>
            )}
            {s.rows.map((r) => (
              <tr key={r.icd} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-2 py-1 tabular-nums whitespace-nowrap">{r.icd}</td>
                <td className="px-2 py-1">{r.byomei}</td>
                <td className="px-2 py-1 text-right tabular-nums font-medium" title={agg.note(r.n_cases, "件")}>{agg.fmtCount(r.n_cases)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
